import React, { useCallback, useState } from 'react'
import { Card, IconButton, Row, colors, BillboardButton, H4, Paragraph, Separator, usePageWidth } from '@fs/zion-ui'
import {
  ContentEdit,
  DocumentPrinted,
  ContentCheck,
  EventBirth,
  EventDeath,
  EventMarriage,
  MenuHome,
  EventBarBatMitzvah,
  EventBurial,
  EventCensus,
  EventChristening,
  EventCourt,
  EventCremation,
  EventDivorce,
  EventHistorical,
  EventImmigration,
  EventMilitaryService,
  EventNaturalization,
  EventNobility,
  EventOccupation,
} from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  getEventLabel,
  getNestedElementById,
  getPersonName,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext } from '../../providers/FamilyReviewContext'
import { getPlaceFieldValues, getDateFieldValues, getBackgroundColor } from '../../utils/utils'
import { showEventsHighlights } from '../../utils/highlightUtils'

import FieldDisplay from './FieldDisplay'

const iconMapping = {
  BIRTH: EventBirth,
  DEATH: EventDeath,
  MARRIAGE: EventMarriage,
  HOME: MenuHome,
  BARMITZVAH: EventBarBatMitzvah,
  BURIAL: EventBurial,
  CENSUS: EventCensus,
  CHRISTENING: EventChristening,
  COURT: EventCourt,
  CREMATION: EventCremation,
  DIVORCE: EventDivorce,
  HISTORICAL: EventHistorical,
  IMMIGRATION: EventImmigration,
  MILITARY_SERVICE: EventMilitaryService,
  NATURALIZATION: EventNaturalization,
  NOBILITY: EventNobility,
  OCCUPATION: EventOccupation,
  RESIDENCE: MenuHome,
  // Add other mappings as needed
}

export default function EventReviewCard({
  event,
  currentTask,
  editEventOverlay,
  reviewPersonEventsOverlay,
  handleCorrectClick,
}) {
  const h = useSlsHighlightsContext()
  const dispatch = useFamilyReviewDispatchContext()
  const state = useFamilyReviewStateContext()
  const zionSlsState = useSlsStateContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()

  const hasBeenReviewed = state.stepThree.eventCards?.find(
    (cardState) => cardState.eventId === event.id
  )?.hasBeenReviewed
  const isFocused = state.selectedEventId === event.id
  const [isHovered, setIsHovered] = useState(false)

  const openEditPanel = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_EVENT_ID', value: event.id })
    editEventOverlay.open()
    reviewPersonEventsOverlay.close()
    showEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      eventIds: [event.id],
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }, [h, dispatch, event.id, editEventOverlay, reviewPersonEventsOverlay, currentTask, state])

  const handleMouseDown = () => {
    setIsHovered(true)

    showEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      eventIds: [event.id],
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const eventPersonId = event?.personId || event?.superElements.find((element) => element.id.includes('1:1'))?.id
  const eventPerson = getNestedElementById(currentTask?.subElements, eventPersonId)
  const nameValue = [
    {
      displayLabel: t('edit-essential-info.title', 'Name'),
      value: eventPerson && getPersonName({ person: eventPerson, justOneName: true }),
      id: eventPersonId,
    },
  ]
  const superElementsLength = event?.superElements?.filter(({ id }) => {
    const isOnCurrentTask = Boolean(getNestedElementById([currentTask], id))
    return isOnCurrentTask
  })?.length
  const placeValues = getPlaceFieldValues(event)
  const dateValues = getDateFieldValues(event)
  const backgroundColor = getBackgroundColor(hasBeenReviewed, isFocused, colors.gray10, colors.yellow10, colors.green10)
  const cardBackgroundColor = getBackgroundColor(
    hasBeenReviewed,
    isFocused,
    colors.white100,
    colors.yellow02,
    colors.white100
  )

  const EventTypeIcon = iconMapping[event.eventType] || DocumentPrinted // Default to DocumentPrinted if no match

  return (
    <Card
      edges={isFocused || isHovered ? 'elevation' : 'border'}
      data-hasbeenreviewed={hasBeenReviewed}
      backgroundColor={cardBackgroundColor}
      onMouseDown={handleMouseDown}
    >
      <Row>
        <EventTypeIcon size={atPageWidth({ default: 'sm', md: 'md' })} background={backgroundColor} noShrinkX />
        <div growX>
          <H4 size="H5">{getEventLabel(event, zionSlsState.types) || t('view-index.event.title', 'Event')}</H4>
          <Paragraph size="sm">
            <FieldDisplay heading={nameValue.displayLabel} values={nameValue} />
            <OtherNames superElementsLength={superElementsLength} t={t} />
            {placeValues && placeValues.length > 0 && (
              <FieldDisplay heading={t('edit-events.fields.place-heading', 'Place')} values={placeValues} />
            )}
            {dateValues && dateValues.length > 0 && (
              <FieldDisplay heading={t('edit-events.fields.date-heading', 'Date')} values={dateValues} />
            )}
          </Paragraph>
        </div>
        {atPageWidth({
          default: (
            <>
              {!hasBeenReviewed && (
                <>
                  <IconButton
                    size="sm"
                    emphasis="high"
                    disabled={!isFocused}
                    Icon={ContentCheck}
                    data-testid="Correct Event Icon"
                    description={t('family-review.step2.correct-button', 'Correct')}
                    linkName="Review Event Correct"
                    onClick={() => handleCorrectClick(event.id)}
                  />
                  <Separator size="xs" />
                </>
              )}
              <IconButton
                size="sm"
                emphasis="medium"
                Icon={ContentEdit}
                data-testid="Edit Event Icon"
                description={t('family-review.step2.edit-button', 'Edit')}
                linkName="Review Event Edit"
                onClick={openEditPanel}
              />
            </>
          ),
          sm: hasBeenReviewed && (
            <IconButton
              size="sm"
              emphasis="medium"
              Icon={ContentEdit}
              data-testid="Edit Event Icon"
              description={t('family-review.step2.edit-button', 'Edit')}
              linkName="Review Event Edit"
              onClick={openEditPanel}
              noShrinkX
            />
          ),
        })}
      </Row>
      {!hasBeenReviewed &&
        atPageWidth({
          default: null,
          sm: (
            <>
              <Separator />
              <Row alignX="center" wrap guttersY="xxs">
                <BillboardButton
                  noShrinkX
                  onClick={openEditPanel}
                  color={isFocused ? 'yellow' : 'blue'}
                  emphasis={isFocused ? 'medium' : 'low'}
                  linkName="Review Event Edit"
                  data-testid="Edit"
                  Icon={ContentEdit}
                >
                  {t('family-review.step2.edit-button', 'Edit')}
                </BillboardButton>
                <BillboardButton
                  noShrinkX
                  onClick={() => handleCorrectClick(event.id)}
                  color={isFocused ? 'yellow' : 'blue'}
                  emphasis={isFocused ? 'high' : 'medium'}
                  linkName="Review Event Correct"
                  data-testid="Correct Event"
                  Icon={ContentCheck}
                >
                  {t('family-review.step2.correct-button', 'Correct')}
                </BillboardButton>
              </Row>
            </>
          ),
        })}
    </Card>
  )
}

function OtherNames({ superElementsLength, t }) {
  return (
    <>
      {superElementsLength > 1 && (
        <span key="some things about other people">
          {superElementsLength > 2
            ? t('edit-events.fields.other-names-plural', '+ {total} Other Names', {
                total: superElementsLength - 1,
              })
            : t('edit-events.fields.other-name-singular', '+ 1 Other Name')}
          <br />
        </span>
      )}
    </>
  )
}
